<script setup lang="ts">
  import { computed } from 'vue';
  import { useField } from 'vee-validate';
  import { useLocale } from '@tundr/i18n';
  import PasswordInput from './PasswordInput.vue';
  import type { PasswordInputProps } from './PasswordInput.types';

  type PasswordInputFieldProps = Omit<PasswordInputProps, 'modelValue'> & {
    name: string;
    rules?: (value: string | null) => string | boolean;
  };

  const props = defineProps<PasswordInputFieldProps>();

  const { t } = useLocale('ds-components');

  const resolvedLabel = computed(
    () => props.label ?? t('password_input.label'),
  );

  const {
    value: fieldValue,
    errorMessage,
    handleChange,
    handleBlur,
    meta,
  } = useField<string | null>(() => props.name, props.rules, {
    label: resolvedLabel,
  });

  const displayError = computed(() =>
    meta.touched ? errorMessage.value : undefined,
  );
</script>

<template>
  <PasswordInput
    :id="id"
    :test-id="testId"
    :label="resolvedLabel"
    :helper-text="helperText"
    :warning="warning"
    :name="name"
    :placeholder="placeholder"
    :disabled="disabled"
    :disabled-reason="disabledReason"
    :required="required"
    :autofocus="autofocus"
    :pattern="pattern"
    :autocomplete="autocomplete"
    :input-mode="inputMode"
    :size="size"
    :model-value="fieldValue ?? null"
    :error="displayError"
    @update:model-value="handleChange"
    @blur="() => handleBlur()"
  >
    <template v-if="$slots.extraLabel" #extraLabel>
      <slot name="extraLabel" />
    </template>
  </PasswordInput>
</template>
